import { useState } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useProjects, usePurchaseOrders } from '@/api/hooks'
import { useDownloadPoPdf } from '@/features/purchase-orders/hooks'
import { DocumentZipButton } from '@/features/manufacturers/document-zip-button'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { ProjectsTable, ProjectsTableSkeleton } from '@/components/projects-table'
import { PurchaseOrdersTable, PurchaseOrdersTableSkeleton } from '@/components/purchase-orders-table'
import { ProjectFilterFields } from '@/components/project-filters'
import { ProjectPriorityFilter } from '@/components/project-priority-filter'
import { ServiceTierFilter } from '@/components/service-tier'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { SearchInput } from '@/components/search-input'
import { activeProjectFilters, projectFilterParams, type ProjectFilterValues } from '@/lib/project-filters'
import { projectPriorityParam, type PROJECT_PRIORITY_FILTER_OPTIONS } from '@/lib/project-priority'
import { serviceTierParam, type SERVICE_TIER_FILTER_OPTIONS } from '@/lib/service-tier'
import { resultCount } from '@/lib/utils'

const PER_PAGE = 20

// Every project with the manufacturer, whatever its state, until a filter narrows it.
const PROJECT_FILTER_DEFAULTS: ProjectFilterValues = {
  status: 'all',
  stage: null,
  automation: 'all',
  source: 'all',
  priority: 'all',
  tier: 'all',
  staff: null,
}

function Pager({ page, pages, onPage }: { page: number; pages: number; onPage: (page: number) => void }) {
  if (pages <= 1) return null
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground">
        Page {page} of {pages}
      </p>
      <div className="flex gap-2">
        <Button disabled={page <= 1} onClick={() => onPage(Math.max(1, page - 1))} size="sm" variant="outline">
          <ChevronLeft className="h-4 w-4" />
          <span className="max-sm:sr-only">Previous</span>
        </Button>
        <Button disabled={page >= pages} onClick={() => onPage(page + 1)} size="sm" variant="outline">
          <span className="max-sm:sr-only">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

function SearchBox({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <SearchInput
        placeholder={`${label}...`}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9"
      />
    </div>
  )
}

function Empty({ children }: { children: string }) {
  return <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">{children}</p>
}

/** Every project buying from the manufacturer, in the Projects page's table and filters (USA-1740). */
export function ManufacturerProjects({ manufacturerUuid }: { manufacturerUuid: string }) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search)
  const [filters, setFilters] = useState(PROJECT_FILTER_DEFAULTS)
  const { showMyProjects, staffId } = useMyProjectsFilter()
  const { data, isLoading } = useProjects({
    manufacturer_uuid: manufacturerUuid,
    search: debouncedSearch || undefined,
    ...projectFilterParams(filters),
    staff_id: staffId ?? filters.staff ?? undefined,
    page,
    limit: PER_PAGE,
  })
  // My Projects narrows the list too, so an empty list then says nothing matches.
  const filtered = !!debouncedSearch || showMyProjects || activeProjectFilters(filters, 'all', showMyProjects) > 0
  return (
    <div className="space-y-3">
      <ListFilters
        count={resultCount(data?.total, 'project')}
        active={activeProjectFilters(filters, 'all', showMyProjects)}
        onClear={() => {
          setFilters(PROJECT_FILTER_DEFAULTS)
          setPage(1)
        }}
        search={
          <SearchBox
            label="Search projects"
            value={search}
            onChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
          />
        }
      >
        <ProjectFilterFields
          values={filters}
          onChange={(change) => {
            setFilters((current) => ({ ...current, ...change }))
            setPage(1)
          }}
          staffLocked={showMyProjects}
          lockedStaffId={staffId}
        />
      </ListFilters>
      {isLoading ? (
        <ProjectsTableSkeleton rows={4} />
      ) : !data?.items.length ? (
        <Empty>{filtered ? 'No projects match' : 'No projects with this manufacturer yet'}</Empty>
      ) : (
        <>
          <ProjectsTable items={data.items} />
          <Pager page={data.page ?? page} pages={data.pages ?? 1} onPage={setPage} />
        </>
      )}
    </div>
  )
}

/** Every PO placed with the manufacturer, in the Purchase Orders page's table and filters (USA-1740). */
export function ManufacturerPurchaseOrders({
  manufacturerUuid,
  manufacturerName,
}: {
  manufacturerUuid: string
  manufacturerName: string
}) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search)
  const [priority, setPriority] = useState<(typeof PROJECT_PRIORITY_FILTER_OPTIONS)[number]>('all')
  const [tier, setTier] = useState<(typeof SERVICE_TIER_FILTER_OPTIONS)[number]>('all')
  const downloadPdf = useDownloadPoPdf()
  const { showMyProjects, opsPersonId } = useMyProjectsFilter()
  const shownFilters = {
    manufacturer_uuid: manufacturerUuid,
    search: debouncedSearch || undefined,
    ops_person_id: opsPersonId,
    project_priority: projectPriorityParam(priority),
    service_tier: serviceTierParam(tier),
  }
  const { data, isLoading } = usePurchaseOrders({ ...shownFilters, page, limit: PER_PAGE })
  const active = (priority === 'all' ? 0 : 1) + (tier === 'all' ? 0 : 1)
  return (
    <div className="space-y-3">
      <ListFilters
        count={resultCount(data?.total, 'purchase order')}
        active={active}
        actions={
          data?.total ? (
            <DocumentZipButton
              path="/dashboard/purchase-orders/download-zip"
              params={shownFilters}
              filename={`${manufacturerName} POs.zip`}
            />
          ) : null
        }
        onClear={() => {
          setPriority('all')
          setTier('all')
          setPage(1)
        }}
        search={
          <SearchBox
            label="Search PO, order, customer, product or tracking"
            value={search}
            onChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
          />
        }
      >
        <ListFilter label="Project priority">
          <ProjectPriorityFilter
            value={priority}
            onValueChange={(value) => {
              setPriority(value)
              setPage(1)
            }}
          />
        </ListFilter>
        <ListFilter label="Service tier">
          <ServiceTierFilter
            value={tier}
            onValueChange={(value) => {
              setTier(value)
              setPage(1)
            }}
          />
        </ListFilter>
      </ListFilters>
      {isLoading ? (
        <PurchaseOrdersTableSkeleton rows={4} />
      ) : !data?.items.length ? (
        <Empty>
          {debouncedSearch || showMyProjects || active > 0 ? 'No purchase orders match' : 'No purchase orders with this manufacturer yet'}
        </Empty>
      ) : (
        <>
          <PurchaseOrdersTable items={data.items} onDownloadPdf={downloadPdf} showManufacturer={false} />
          <Pager page={data.page ?? page} pages={data.pages ?? 1} onPage={setPage} />
        </>
      )}
    </div>
  )
}
